/**
 * MembershipStatus — renders a user's PLQ membership state.
 *
 * variant="full"    → bordered card with member number, last-checked
 *                     timestamp, and renewal instructions (used on
 *                     /settings/profile/carte).
 * variant="compact" → single-line inline badge (used in the avatar sheet).
 *
 * Returns null when `plqMemberId` is null (legacy / unlinked accounts) in
 * both variants — the caller decides whether to render a fallback.
 *
 * Status states: active (green) / expiring-soon (amber, within
 * EXPIRY_WARNING_DAYS) / expired (red) / cancelled (red).
 */

type MembershipUser = {
	plqMemberId: number | null
	membershipExpiresAt: Date | string | null
	membershipCheckedAt?: Date | string | null
}

type MembershipStatusProps = {
	variant: 'full' | 'compact'
	data: MembershipUser | null | undefined
}

// How many days before échéance the badge flips to an amber "à renouveler"
// warning. 30 days is the membership-renewal standard and leaves room to
// renew before losing access in the run-up to the election.
const EXPIRY_WARNING_DAYS = 30
const DAY_MS = 1000 * 60 * 60 * 24

function computeStatus(data: MembershipUser) {
	const expiresAt = data.membershipExpiresAt
		? new Date(data.membershipExpiresAt)
		: null

	// `refreshMembershipFromPLQ` uses `new Date(0)` as a sentinel for
	// members flagged `cancelled` or `deleted` upstream.
	const isCancelled = expiresAt !== null && expiresAt.getTime() === 0
	const msUntilExpiry =
		expiresAt !== null && !isCancelled
			? expiresAt.getTime() - Date.now()
			: null
	const isActive = msUntilExpiry !== null && msUntilExpiry > 0
	const isExpiringSoon =
		isActive && msUntilExpiry <= EXPIRY_WARNING_DAYS * DAY_MS
	const daysUntilExpiry = isActive ? Math.ceil(msUntilExpiry / DAY_MS) : null

	return { expiresAt, isCancelled, isActive, isExpiringSoon, daysUntilExpiry }
}

const formatDate = (d: Date) =>
	d.toLocaleDateString('fr-CA', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	})

const pluralJours = (n: number) => `${n} jour${n > 1 ? 's' : ''}`

export function MembershipStatus({ variant, data }: MembershipStatusProps) {
	// Hide for unlinked / legacy accounts.
	if (!data || data.plqMemberId === null) return null

	const status = computeStatus(data)

	if (variant === 'compact') {
		return <CompactMembershipStatus {...status} />
	}

	return <FullMembershipStatus data={data} {...status} />
}

type ComputedStatus = ReturnType<typeof computeStatus>

function CompactMembershipStatus({
	isActive,
	isCancelled,
	isExpiringSoon,
	daysUntilExpiry,
	expiresAt,
}: ComputedStatus) {
	if (isActive) {
		if (isExpiringSoon) {
			return (
				<span className="text-sm text-amber-600 dark:text-amber-400">
					⚠ Adhésion à renouveler — expire le {formatDate(expiresAt!)}
					{daysUntilExpiry !== null
						? ` (dans ${pluralJours(daysUntilExpiry)})`
						: ''}
				</span>
			)
		}
		return (
			<span className="text-sm text-green-600 dark:text-green-400">
				✓ Adhésion active jusqu'au {formatDate(expiresAt!)}
			</span>
		)
	}
	if (isCancelled) {
		return (
			<span className="text-sm text-red-600 dark:text-red-400">
				✗ Adhésion annulée
			</span>
		)
	}
	return (
		<span className="text-sm text-red-600 dark:text-red-400">
			✗ Adhésion expirée
			{expiresAt ? ` depuis le ${formatDate(expiresAt)}` : ''}
		</span>
	)
}

function FullMembershipStatus({
	data,
	isActive,
	isCancelled,
	isExpiringSoon,
	daysUntilExpiry,
	expiresAt,
}: ComputedStatus & { data: MembershipUser }) {
	let statusLabel: React.ReactNode
	if (isActive) {
		statusLabel = isExpiringSoon ? (
			<span className="text-amber-600 dark:text-amber-400">
				⚠ Adhésion à renouveler — expire le {formatDate(expiresAt!)}
				{daysUntilExpiry !== null
					? ` (dans ${pluralJours(daysUntilExpiry)})`
					: ''}
			</span>
		) : (
			<span className="text-green-600 dark:text-green-400">
				✓ Adhésion active jusqu'au {formatDate(expiresAt!)}
			</span>
		)
	} else if (isCancelled) {
		statusLabel = (
			<span className="text-red-600 dark:text-red-400">✗ Adhésion annulée</span>
		)
	} else {
		statusLabel = (
			<span className="text-red-600 dark:text-red-400">
				✗ Adhésion expirée
				{expiresAt ? ` depuis le ${formatDate(expiresAt)}` : ''}
			</span>
		)
	}

	const checkedAt = data.membershipCheckedAt
		? new Date(data.membershipCheckedAt)
		: null

	return (
		<div className="border-border bg-card rounded-lg border p-6">
			<h2 className="text-lg font-semibold">Adhésion PLQ</h2>
			<p className="text-muted-foreground mt-1 text-sm">
				Numéro de membre : <strong>{data.plqMemberId}</strong>
			</p>
			<p className="mt-2 text-sm">{statusLabel}</p>
			{checkedAt ? (
				<p className="text-muted-foreground mt-1 text-xs">
					Dernière vérification : {checkedAt.toLocaleString('fr-CA')}
				</p>
			) : null}
			{!isActive ? (
				<p className="mt-3 text-sm">
					{isCancelled
						? 'Contactez le PLQ pour réactiver votre adhésion, puis rechargez cette page pour débloquer votre accès.'
						: 'Renouvelez votre adhésion depuis le tableau de bord du PLQ, puis rechargez cette page pour débloquer votre accès.'}
				</p>
			) : isExpiringSoon ? (
				<p className="mt-3 text-sm">
					Pensez à renouveler votre adhésion depuis le tableau de bord du PLQ
					avant l&apos;échéance pour conserver votre accès sans interruption.
				</p>
			) : null}
		</div>
	)
}
